// src/components/YourModule/SalesQuotationModule/AllSalesQuotations.jsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import {
  FileText,
  Search,
  XCircle,
  BarChart2,
  User,
  Calendar,
  DollarSign,
  Printer,
  ShoppingCart,
  CheckCircle,
  AlertCircle,
  MapPin,
  ChevronDown,
  ChevronRight,
  Layers,
  Package,
  Hash,
  Info,
  Clock,
} from "lucide-react";

import Loader from "../../../loader/Loader";
import AddSalesOrderInQuotation from "./AddSalesOrderInQuotation";
import Pagination from "../../Pagination";

import { domain } from "../../../../security";
import SalesQuotationPrint from "../../POS/SalesQuotationPrint";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const currentStatus = status || "Open";
  let color = "bg-blue-50 text-blue-700 border-blue-200";
  let icon = <FileText size={11} />;

  if (currentStatus === "Converted") {
    color = "bg-green-50 text-green-700 border-green-200";
    icon = <CheckCircle size={11} />;
  } else if (currentStatus === "Cancelled") {
    color = "bg-red-50 text-red-700 border-red-200";
    icon = <XCircle size={11} />;
  } else if (currentStatus === "Open") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    icon = <Clock size={11} />;
  }

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}
    >
      {icon}
      <span>{currentStatus}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MOBILE CARD
// ─────────────────────────────────────────────────────────────

const QuotationCard = ({ quote, onCancel, onPrint, onConvertToOrder }) => {
  const [expanded, setExpanded] = useState(false);
  const isLocked = quote.status === "Converted" || quote.status === "Cancelled";

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-1.5 bg-indigo-50 rounded-lg shrink-0">
              <FileText size={15} className="text-indigo-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 text-sm">
                  {quote.quoteNumber}
                </h3>
                {expanded ? (
                  <ChevronDown size={14} className="text-gray-400" />
                ) : (
                  <ChevronRight size={14} className="text-gray-400" />
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">
                {quote.customer?.customerName || "Unknown Customer"}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <StatusBadge status={quote.status} />
            <p className="text-sm font-bold text-gray-900 mt-1.5">
              {formatCurrency(quote.totalAmount)}
            </p>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3">
          {/* Meta info */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-gray-400 uppercase font-semibold block mb-0.5">
                Location
              </span>
              <p className="text-gray-700 font-medium">
                {quote.locationName || "N/A"}
              </p>
            </div>
            <div>
              <span className="text-gray-400 uppercase font-semibold block mb-0.5">
                Date
              </span>
              <p className="text-gray-700 font-medium">
                {formatDate(quote.date)}
              </p>
            </div>
            {quote.preparedBy && (
              <div>
                <span className="text-gray-400 uppercase font-semibold block mb-0.5">
                  Prepared By
                </span>
                <p className="text-gray-700 font-medium">{quote.preparedBy}</p>
              </div>
            )}
            {quote.checkedBy && (
              <div>
                <span className="text-gray-400 uppercase font-semibold block mb-0.5">
                  Checked By
                </span>
                <p className="text-gray-700 font-medium">{quote.checkedBy}</p>
              </div>
            )}
          </div>

          {/* Line items */}
          {(quote.quotationProducts || []).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <Package size={12} /> Items ({quote.quotationProducts.length})
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {(quote.quotationProducts || []).map((p, idx) => (
                  <div key={p.id || idx} className="px-3 py-2 text-xs">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">
                          {p.productName}
                        </p>
                        <span className="inline-flex items-center gap-0.5 text-indigo-600 font-mono bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 text-[10px] mt-0.5">
                          <Hash size={8} />
                          {p.itemCode || "—"}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-gray-900">
                          {formatCurrency(p.subtotal)}
                        </p>
                        <p className="text-gray-400">
                          {p.quantity} {p.uom || ""} × {formatCurrency(p.price)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs font-bold text-gray-500">Total</span>
                <span className="text-sm font-bold text-gray-900">
                  {formatCurrency(quote.totalAmount)}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            {!isLocked && (
              <button
                onClick={() => onConvertToOrder(quote)}
                className="flex-1 py-2 border border-indigo-200 text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-xs font-bold flex justify-center items-center gap-1 transition"
              >
                <ShoppingCart size={13} /> Generate SO
              </button>
            )}
            <button
              onClick={() => onPrint(quote)}
              className="flex-1 py-2 border border-slate-200 text-slate-600 rounded-lg bg-white hover:bg-slate-50 text-xs font-bold flex justify-center items-center gap-1 transition"
            >
              <Printer size={13} /> Print
            </button>
            {!isLocked && (
              <button
                onClick={() => onCancel(quote.id)}
                className="flex-1 py-2 border border-red-100 text-red-600 rounded-lg bg-red-50 hover:bg-red-100 text-xs font-bold flex justify-center items-center gap-1 transition"
              >
                <XCircle size={13} /> Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const AllSalesQuotations = () => {
  const [quotationData, setQuotationData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [activeTab, setActiveTab] = useState("Open");
  const [expandedQuote, setExpandedQuote] = useState(null); // ← expandable row state

  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [selectedQuoteForOrder, setSelectedQuoteForOrder] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [quotePrintData, setQuotePrintData] = useState(null);
  const quotePrintRef = useRef();

  const handlePrintAction = useReactToPrint({
    content: () => quotePrintRef.current,
    documentTitle: "Sales Quotation",
  });

  const handlePrintClick = (quote) => {
    setQuotePrintData(quote);
    setTimeout(() => handlePrintAction(), 200);
  };

  const locations = useMemo(() => {
    const unique = [
      ...new Set(quotationData.map((q) => q.locationName).filter(Boolean)),
    ].sort();
    return unique.length > 0 ? [...unique, "All"] : ["All"];
  }, [quotationData]);

  useEffect(() => {
    if (locations.length > 0 && selectedLocation === "") {
      setSelectedLocation(locations[0] !== "All" ? locations[0] : "All");
    }
  }, [locations, selectedLocation]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${domain}/api/SalesQuotations`);
      setQuotationData(response.data);
    } catch {
      toast.error("Failed to fetch Sales Quotations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredQuotes = useMemo(() => {
    return quotationData.filter((q) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        q.quoteNumber?.toLowerCase().includes(searchLower) ||
        q.customer?.customerName?.toLowerCase().includes(searchLower) ||
        q.fullName?.toLowerCase().includes(searchLower);

      const matchesLocation =
        selectedLocation === "All" ||
        q.locationName === selectedLocation ||
        selectedLocation === "";

      const currentStatus = q.status || "Open";
      const matchesTab =
        activeTab === "All" ? true : currentStatus === activeTab;

      return matchesSearch && matchesLocation && matchesTab;
    });
  }, [searchTerm, quotationData, selectedLocation, activeTab]);

  const totalAmount = filteredQuotes.reduce(
    (sum, item) => sum + (item.totalAmount || 0),
    0,
  );

  const cancelQuotation = async (id) => {
    if (!window.confirm("Are you sure you want to CANCEL this quotation?"))
      return;
    try {
      await axios.put(`${domain}/api/SalesQuotations/cancel/${id}`, {});
      toast.success("Quotation Cancelled Successfully!");
      if (expandedQuote === id) setExpandedQuote(null);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to cancel Quotation.",
      );
    }
  };

  const openOrderModal = (quote) => {
    if (quote.status === "Converted") {
      toast.info("Already converted to Order.");
      return;
    }
    if (quote.status === "Cancelled") {
      toast.error("Quotation is cancelled.");
      return;
    }
    setSelectedQuoteForOrder(quote);
    setIsOrderModalVisible(true);
  };

  const closeOrderModal = () => {
    setIsOrderModalVisible(false);
    setSelectedQuoteForOrder(null);
  };

  const toggleExpandQuote = (id) =>
    setExpandedQuote(expandedQuote === id ? null : id);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredQuotes.slice(indexOfFirstItem, indexOfLastItem);

  // Tab counts
  const openCount = quotationData.filter(
    (q) => (q.status || "Open") === "Open",
  ).length;
  const convertedCount = quotationData.filter(
    (q) => q.status === "Converted",
  ).length;
  const cancelledCount = quotationData.filter(
    (q) => q.status === "Cancelled",
  ).length;

  const tabs = [
    {
      id: "Open",
      label: "Open",
      count: openCount,
      icon: <FileText size={13} />,
    },
    {
      id: "Converted",
      label: "Converted",
      count: convertedCount,
      icon: <CheckCircle size={13} />,
    },
    {
      id: "Cancelled",
      label: "Cancelled",
      count: cancelledCount,
      icon: <XCircle size={13} />,
    },
    {
      id: "All",
      label: "All Quotes",
      count: quotationData.length,
      icon: <Layers size={13} />,
    },
  ];

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <FileText size={22} className="text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Sales Quotations
                </h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  Manage customer quotations · Convert to Sales Orders
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentPage(1);
                  setExpandedQuote(null);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.id
                      ? "bg-indigo-50 text-indigo-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-wide">
                Filtered Quotes
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredQuotes.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex items-center gap-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <DollarSign size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-wide">
                Filtered Value
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Search + Location */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Quote # or Customer..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all text-sm outline-none"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="relative min-w-[200px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 appearance-none bg-white text-sm outline-none"
              >
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc === "All" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            {/* ── Desktop Expandable Table ── */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">Quote #</th>
                    <th className="px-5 py-3.5 font-semibold">Customer</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold text-right">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentItems.map((item) => {
                    const isLocked =
                      item.status === "Converted" ||
                      item.status === "Cancelled";
                    const isExpanded = expandedQuote === item.id;

                    return (
                      <React.Fragment key={item.id}>
                        {/* ── Main Row ── */}
                        <tr
                          onClick={() => toggleExpandQuote(item.id)}
                          className={`hover:bg-indigo-50/30 transition cursor-pointer ${isExpanded ? "bg-indigo-50/20" : ""}`}
                        >
                          {/* Expand toggle */}
                          <td className="px-5 py-3.5 text-center">
                            <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                              {isExpanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>

                          {/* Quote # */}
                          <td className="px-5 py-3.5">
                            <span className="font-bold text-gray-900">
                              {item.quoteNumber}
                            </span>
                          </td>

                          {/* Customer */}
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-900">
                              {item.customer?.customerName ||
                                item.fullName ||
                                "Unknown"}
                            </p>
                            <p className="text-xs text-gray-400">
                              {item.locationName || "N/A"}
                            </p>
                          </td>

                          {/* Date */}
                          <td className="px-5 py-3.5 text-gray-500">
                            {formatDate(item.date)}
                          </td>

                          {/* Status */}
                          <td className="px-5 py-3.5">
                            <StatusBadge status={item.status} />
                          </td>

                          {/* Amount */}
                          <td className="px-5 py-3.5 text-right font-bold text-gray-900">
                            {formatCurrency(item.totalAmount)}
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-3.5">
                            <div
                              className="flex items-center justify-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!isLocked && (
                                <button
                                  onClick={() => openOrderModal(item)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition shadow-sm whitespace-nowrap"
                                >
                                  <ShoppingCart size={13} /> Generate SO
                                </button>
                              )}
                              <button
                                onClick={() => handlePrintClick(item)}
                                title="Print"
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                              >
                                <Printer size={16} />
                              </button>
                              {!isLocked && (
                                <button
                                  onClick={() => cancelQuotation(item.id)}
                                  title="Cancel"
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                >
                                  <XCircle size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* ── Expanded Detail Row ── */}
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan="7"
                              className="bg-slate-50/70 px-6 py-5"
                            >
                              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                {/* Meta */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
                                  {[
                                    {
                                      icon: <MapPin size={11} />,
                                      label: "Location",
                                      value: item.locationName || "N/A",
                                    },
                                    {
                                      icon: <Calendar size={11} />,
                                      label: "Quote Date",
                                      value: formatDate(item.date),
                                    },
                                    {
                                      icon: <User size={11} />,
                                      label: "Prepared By",
                                      value: item.preparedBy || "N/A",
                                    },
                                    {
                                      icon: <User size={11} />,
                                      label: "Checked By",
                                      value: item.checkedBy || "—",
                                    },
                                  ].map((info) => (
                                    <div key={info.label} className="space-y-1">
                                      <span className="text-xs text-gray-400 uppercase font-semibold flex items-center gap-1">
                                        {info.icon} {info.label}
                                      </span>
                                      <p className="text-sm font-medium text-gray-800">
                                        {info.value}
                                      </p>
                                    </div>
                                  ))}

                                  {/* Customer details spanning full width */}
                                  {item.customer && (
                                    <div className="col-span-2 md:col-span-4 bg-indigo-50/60 p-3 rounded-lg border border-indigo-100">
                                      <span className="text-xs text-indigo-700 font-bold uppercase block mb-2">
                                        Customer Details
                                      </span>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div>
                                          <span className="text-gray-400 block">
                                            Name
                                          </span>
                                          <span className="font-semibold text-gray-800">
                                            {item.customer.customerName}
                                          </span>
                                        </div>
                                        {item.customer.address && (
                                          <div>
                                            <span className="text-gray-400 block">
                                              Address
                                            </span>
                                            <span className="font-semibold text-gray-800">
                                              {item.customer.address}
                                            </span>
                                          </div>
                                        )}
                                        {item.customer.tinNumber && (
                                          <div>
                                            <span className="text-gray-400 block">
                                              TIN
                                            </span>
                                            <span className="font-semibold text-gray-800">
                                              {item.customer.tinNumber}
                                            </span>
                                          </div>
                                        )}
                                        {item.customer.mobileNumber && (
                                          <div>
                                            <span className="text-gray-400 block">
                                              Mobile
                                            </span>
                                            <span className="font-semibold text-gray-800">
                                              {item.customer.mobileNumber}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Items header */}
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide flex items-center gap-1.5">
                                    <Package size={14} /> Quotation Items
                                  </h4>
                                  <span className="text-xs text-gray-400">
                                    {(item.quotationProducts || []).length}{" "}
                                    item(s)
                                  </span>
                                </div>

                                {/* ── Line Items Table ── */}
                                <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
                                  <table className="w-full text-xs text-left text-gray-600">
                                    <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100">
                                      <tr>
                                        <th className="px-4 py-2.5">
                                          Item Code
                                        </th>
                                        <th className="px-4 py-2.5">Product</th>
                                        <th className="px-4 py-2.5 text-center">
                                          Qty
                                        </th>
                                        <th className="px-4 py-2.5 text-center">
                                          UOM
                                        </th>
                                        <th className="px-4 py-2.5 text-right">
                                          Unit Price
                                        </th>
                                        <th className="px-4 py-2.5 text-right">
                                          Subtotal
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {(item.quotationProducts || []).length ===
                                      0 ? (
                                        <tr>
                                          <td
                                            colSpan="6"
                                            className="px-4 py-6 text-center text-gray-400"
                                          >
                                            No items on this quotation.
                                          </td>
                                        </tr>
                                      ) : (
                                        (item.quotationProducts || []).map(
                                          (p, idx) => (
                                            <tr
                                              key={p.id || idx}
                                              className="hover:bg-gray-50 transition"
                                            >
                                              {/* Item Code */}
                                              <td className="px-4 py-2.5">
                                                <span className="inline-flex items-center gap-1 text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                                  <Hash size={10} />
                                                  {p.itemCode || "—"}
                                                </span>
                                              </td>
                                              {/* Product */}
                                              <td className="px-4 py-2.5 font-medium text-gray-900">
                                                {p.productName}
                                                {p.hasSerial && (
                                                  <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold uppercase">
                                                    Serial
                                                  </span>
                                                )}
                                                {p.priceType && (
                                                  <span className="ml-1.5 text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold uppercase">
                                                    {p.priceType}
                                                  </span>
                                                )}
                                              </td>
                                              {/* Qty */}
                                              <td className="px-4 py-2.5 text-center text-gray-700 font-bold">
                                                {p.quantity}
                                              </td>
                                              {/* UOM */}
                                              <td className="px-4 py-2.5 text-center text-gray-500">
                                                {p.uom || "—"}
                                              </td>
                                              {/* Unit Price */}
                                              <td className="px-4 py-2.5 text-right">
                                                {formatCurrency(p.price)}
                                              </td>
                                              {/* Subtotal */}
                                              <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                                                {formatCurrency(p.subtotal)}
                                              </td>
                                            </tr>
                                          ),
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {/* Total summary */}
                                <div className="flex justify-end">
                                  <div className="w-full md:w-64 bg-gray-50 p-4 rounded-xl border border-gray-200 text-sm space-y-2">
                                    <div className="flex justify-between text-gray-500">
                                      <span>Total Qty</span>
                                      <span className="font-medium text-gray-700">
                                        {item.totalQuantity || 0}
                                      </span>
                                    </div>
                                    <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
                                      <span>Grand Total</span>
                                      <span>
                                        {formatCurrency(item.totalAmount)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Footer actions */}
                                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-2">
                                  <button
                                    onClick={() => handlePrintClick(item)}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-100 transition"
                                  >
                                    <Printer size={13} /> Print
                                  </button>
                                  {!isLocked && (
                                    <>
                                      <button
                                        onClick={() => cancelQuotation(item.id)}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 transition"
                                      >
                                        <XCircle size={13} /> Cancel
                                      </button>
                                      <button
                                        onClick={() => openOrderModal(item)}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition shadow-sm"
                                      >
                                        <ShoppingCart size={13} /> Generate SO
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No quotations found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm
                              ? "Try a different search term."
                              : "No records in this category."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Mobile Cards ── */}
            <div className="lg:hidden space-y-3">
              {currentItems.map((item) => (
                <QuotationCard
                  key={item.id}
                  quote={item}
                  onCancel={cancelQuotation}
                  onPrint={handlePrintClick}
                  onConvertToOrder={openOrderModal}
                />
              ))}
              {currentItems.length === 0 && (
                <div className="text-center py-14 text-gray-400 bg-white rounded-2xl border border-gray-100">
                  <AlertCircle size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="font-medium text-gray-500">
                    No quotations found.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredQuotes.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      {isOrderModalVisible && (
        <AddSalesOrderInQuotation
          onClose={closeOrderModal}
          sourceQuote={selectedQuoteForOrder}
          refreshData={fetchData}
        />
      )}

      <div style={{ display: "none" }}>
        <SalesQuotationPrint ref={quotePrintRef} data={quotePrintData} />
      </div>
    </div>
  );
};

export default AllSalesQuotations;
